import { SiteRoute } from '@journeyapps/package-types';
import * as React from 'react';
import { Link } from 'react-router';
import styled from '@emotion/styled';
import { CrumbWidget } from '../widgets/CrumbWidget';
import { ContentWidget } from '../widgets/ContentWidget';
import { PageWidget } from '../widgets/layout/PageWidget';
import { SectionWidget } from '../widgets/layout/SectionWidget';
import { ProductDocsWidget } from '../widgets/ProductDocsWidget';
import { PHONE_MEDIA } from '../vars';

const imageHero = require('../../media/ai/codex-and-oxide.png');
const imageCli = require('../../media/ai/platform-cli.png');
const imageAppTable = require('../../media/ai/app-table.png');
const imageAppDialog = require('../../media/ai/app-dialog.png');
const imageGetStarted = require('../../media/ai/get-started.png');
const imageLiveEditing = require('../../media/oxide/co-editing.jpg');

export const AIPage: React.FC = () => {
  return (
    <PageWidget width={1100} title="AI">
      <ContentWidget>
        <h1>
          <CrumbWidget parts={['AI']} />
        </h1>
        <p>
          JourneyApps is designed so AI can work with the platform without compromising the developer experience.
          Developers still work with real code, real drafts, real deployments and the same platform stack they already
          know. The difference is that tools like Codex and Claude can now participate in that workflow in a way that is
          deterministic, predictable and easy to control.
        </p>
        <p>
          The result is an AI workflow that feels additive rather than invasive: humans can keep working inside{' '}
          <Link to={SiteRoute.OXIDE}>OXIDE</Link>, while AI uses the platform intelligently through a dedicated CLI and
          a platform-aware skill set.
        </p>
        <S.HeroImage src={imageHero} />

        <SectionWidget
          index={1}
          image={imageCli}
          label="A powerful CLI for existing AI tools"
          content={
            <>
              The main integration point is the Journey Platform CLI. It gives AI tools a stable, platform-aware way to
              inspect projects, work with drafts, inspect deployments, drill into logs and fetch official docs directly
              from the platform docs source.
              <br />
              <br />
              That means existing AI tools can work extremely well with the platform without needing custom browser
              automation or brittle, one-off scripts. The CLI is what makes the workflow repeatable across developers,
              CI and AI agents.
            </>
          }
        />

        <S.ResultBlock>
          <h2>Predictable platform abstractions lead to predictable app outcomes</h2>
          <p>
            The platform stack gives AI strong rails to work within: schema, views, JourneyScript, drafts, deployments
            and docs all map to clear product abstractions. That makes it much easier for AI to build real features in a
            controlled way instead of producing disconnected edits.
          </p>
          <S.ResultExplanation>
            <S.Quote>
              <p>
                use the Journey Draft Editor to build todolist capability in the current app. Use a table for the todos,
                have them editable inline and use a dialog for the form
              </p>
            </S.Quote>
          </S.ResultExplanation>
          <S.ResultImages>
            <S.ResultImage src={imageAppTable} alt="AI-built Journey app table view" />
            <S.ResultImage src={imageAppDialog} alt="AI-built Journey app dialog form" />
          </S.ResultImages>
          <S.ResultSummary>
            We fully expected that prompt to produce a Journey view built around an{' '}
            <S.InlineCode>object-table</S.InlineCode> and a Journey <S.InlineCode>dialog</S.InlineCode>. The screenshots
            above show that outcome with 100% accuracy: an inline-editable todo table and a dialog-based form, exactly
            as requested.
          </S.ResultSummary>
        </S.ResultBlock>

        <SectionWidget
          index={2}
          ltr={false}
          image={imageLiveEditing}
          label="AI works well with humans inside OXIDE"
          content={
            <>
              Because the platform supports live drafts and OXIDE-native editing workflows, AI can collaborate with
              humans instead of replacing them. A tool can pull the active draft, make focused changes, push those
              changes back into the same draft, and let the developer review and commit inside the IDE.
              <br />
              <br />
              This keeps the editing experience familiar and code-centric. Developers still get the full benefit of
              traditional coding, visual editing, and OXIDE's live workflow while AI accelerates the implementation.
            </>
          }
        />

        <SectionWidget
          index={3}
          image={imageGetStarted}
          label="Easy to start, easy to extend"
          content={
            <>
              The workflow is easy to start as well. New projects can be created and then immediately worked on with AI
              using the same CLI and skill flow. Once a project exists, the same commands scale up naturally into draft
              collaboration, deployment inspection and official docs retrieval.
              <br />
              <br />
              That combination of easy onboarding, strong abstractions and a real CLI is what makes the JourneyApps
              Platform a practical home for AI-assisted development.
            </>
          }
        />

        <h2>Why this works</h2>
        <ul>
          <li>It does not compromise traditional coding or the developer editing experience.</li>
          <li>It plays well with existing AI tools because the CLI is a strong integration layer.</li>
          <li>It is predictable because the whole platform stack exposes clear abstractions.</li>
          <li>It is easy to get started with new projects and then grow into more advanced workflows.</li>
          <li>It supports human and AI collaboration inside OXIDE through drafts and live editing flows.</li>
        </ul>
        <p>
          This work builds on the principles in our <Link to={SiteRoute.AI_MANIFESTO}>AI Manifesto</Link>: AI should be
          configurable, additive, transparent and aligned with how real developers want to work.
        </p>
      </ContentWidget>
      <ProductDocsWidget
        title="Build with the Platform CLI"
        description="Get started with installation, authentication, AI workflows, and practical command patterns."
        topics={['Platform CLI']}
      />
    </PageWidget>
  );
};

namespace S {
  export const Quote = styled.blockquote`
    max-width: 600px;
    margin: auto;
  `;
  export const HeroImage = styled.img`
    width: 100%;
    border-radius: 20px;
    border: solid 1px rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 240px -120px rgba(95, 156, 255, 0.55);
    margin-top: 20px;
    margin-bottom: 20px;
  `;

  export const ResultBlock = styled.div`
    padding-top: 40px;
    padding-bottom: 20px;
  `;

  export const ResultImages = styled.div`
    display: flex;
    column-gap: 24px;
    row-gap: 24px;
    margin-top: 24px;

    @media (max-width: ${PHONE_MEDIA}px) {
      flex-direction: column;
    }
  `;

  export const ResultExplanation = styled.div`
    margin-top: 36px;
    margin-bottom: 36px;
  `;

  export const ResultImage = styled.img`
    width: calc(50% - 12px);
    border-radius: 20px;
    border: solid 1px rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 200px -120px rgba(95, 156, 255, 0.45);

    @media (max-width: ${PHONE_MEDIA}px) {
      width: 100%;
    }
  `;

  export const ResultSummary = styled.p`
    margin-top: 40px !important;
  `;

  export const InlineCode = styled.code`
    font-size: 0.95em;
  `;
}
